import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useColorModeValue,
  Text,
  useToast,
  Icon,
  SimpleGrid,
  Divider,
} from '@chakra-ui/react';
import { EmailIcon } from '@chakra-ui/icons';
import * as SharingUtils from '../utils/sharingUtils';
import { submitSupportRequest, validateSupportRequest } from '../services/SupportService';

// Social media icons using simple styled boxes
const FacebookIcon = () => (
  <Box
    bg="#1877F2"
    color="white"
    borderRadius="md"
    p={3}
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="50px"
    h="50px"
    fontSize="2xl"
    fontWeight="bold"
    cursor="pointer"
    _hover={{ transform: 'scale(1.05)', shadow: 'lg' }}
    transition="all 0.2s"
  >
    f
  </Box>
);

const InstagramIcon = () => (
  <Box
    bg="linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)"
    color="white"
    borderRadius="md"
    p={3}
    display="flex"
    alignItems="center"
    justifyContent="center"
    w="50px"
    h="50px"
    fontSize="2xl"
    fontWeight="bold"
    cursor="pointer"
    _hover={{ transform: 'scale(1.05)', shadow: 'lg' }}
    transition="all 0.2s"
  >
    @
  </Box>
);

const Support = () => {
  const [email, setEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleSubmit = async () => {
    if (isSubmitting) {
      return;
    }

    // Validate the form data
    const validationError = validateSupportRequest({ email, question });
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const success = await submitSupportRequest({ email, question });
      
      if (success) {
        toast({
          title: 'Question Submitted Successfully',
          description: 'We\'ll get back to you via email within 24 hours.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Reset form
        setEmail('');
        setQuestion('');
      } else {
        setError('Failed to submit your question. Please try again.');
      }
    } catch (error) {
      setError('Failed to submit your question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialClick = (platform: string) => {
    if (platform === 'facebook') {
      window.open('https://www.facebook.com/MommaFuel', '_blank');
    } else if (platform === 'instagram') {
      window.open('https://www.instagram.com/MommaFuel', '_blank');
    }
  };

  return (
    <Container maxW="lg" py={10}>
      <VStack spacing={8} align="stretch">
        {/* Header Section */}
        <Box textAlign="center">
          <Heading size="xl" color="mamaFuel.moss.700" >
            Contact Us
          </Heading>
        </Box>

        {/* <Box
          bg={bgColor}
          p={6}
          borderRadius="md"
          boxShadow="md"
          border="1px"
          borderColor={borderColor}
        >
          <VStack spacing={4}>
            <Heading size="md" color="mamaFuel.moss.700">
              Follow Us
            </Heading>
            <Text textAlign="center" color="gray.600">
              Stay connected with MommaFuel on social media
            </Text>
            <HStack spacing={6} justify="center">
              <VStack spacing={2}>
                <Box onClick={() => handleSocialClick('facebook')}>
                  <FacebookIcon />
                </Box>
                <Text fontSize="sm" fontWeight="medium">Facebook</Text>
              </VStack>
              <VStack spacing={2}>
                <Box onClick={() => handleSocialClick('instagram')}>
                  <InstagramIcon />
                </Box>
                <Text fontSize="sm" fontWeight="medium">Instagram</Text>
              </VStack>
            </HStack>
          </VStack>
        </Box> */}

        {/* Contact Form Section */}
        <Box
          bg={bgColor}
          p={6}
          borderRadius="md"
          boxShadow="md"
          border="1px"
          borderColor={borderColor}
        >
          <VStack spacing={6} align="stretch">
            <Box textAlign="center">
              <Heading size="md" color="mamaFuel.moss.700" mb={2}>
                Send Us a Message
              </Heading>
              <Text color="gray.600">
                Have a question or need assistance? We'll get back to you within 24 hours.
              </Text>
            </Box>
            
            <FormControl isRequired>
              <FormLabel>Your Email Address</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setError('')}
                placeholder="So we can reply to you"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Your Question</FormLabel>
              <Textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onFocus={() => setError('')}
                placeholder="Tell us how we can help you..."
                rows={5}
                resize="vertical"
              />
            </FormControl>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Button
              bg="mamaFuel.moss.700"
              color="white"
              _hover={{ bg: "mamaFuel.moss.600" }}
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Sending..."
              disabled={isSubmitting}
              size="lg"
            >
              Send Message
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};

export default Support;